import is from '@sindresorhus/is';

import { createUrlTileDefinition, type UrlTileDefinition, type UrlTileRenderContext } from '../../url-tiles';
import { ArchivedItemsPanel } from '../archive-sidepanel/ArchivedItemsPanel';
import { ChangelogDetails } from '../changelog-sidepanel/Details';
import { CompareVersion } from '../compare/CompareVersion';
import { JobClusterDetails } from '../job-cluster-sidepanel/Details';
import { JobFamilyDetails } from '../job-family-sidepanel/Details';
import { JobArchitectureScopeProvider } from '../url-navigation/job-architecture-scope';
import {
    JSA_ARCHIVE_TILE_MAX_WIDTH,
    JSA_ARCHIVE_TILE_SLOT_KEY,
    JSA_ARCHIVE_TILE_TYPE,
    JSA_CHANGELOG_TILE_SLOT_KEY,
    JSA_CHANGELOG_TILE_TYPE,
    JSA_CHANGELOG_TILE_WIDTH,
    JSA_CLUSTER_DETAIL_TILE_TYPE,
    JSA_COMPARE_TILE_SLOT_KEY,
    JSA_COMPARE_TILE_TYPE,
    JSA_FAMILY_DETAIL_TILE_TYPE,
    JSA_PRIMARY_DETAIL_TILE_SLOT_KEY,
    JSA_PROFILE_DETAIL_TILE_TYPE,
    type JsaArchiveTilePayload,
    type JsaChangelogTilePayload,
    type JsaClusterDetailTilePayload,
    type JsaCompareTilePayload,
    type JsaFamilyDetailTilePayload,
    type JsaProfileDetailTilePayload,
    jsaArchiveTileSchema,
    jsaChangelogTileSchema,
    jsaClusterDetailTileSchema,
    jsaCompareTileSchema,
    jsaFamilyDetailTileSchema,
    jsaProfileDetailTileSchema,
    versionTokenToNumber,
    versionTokenToString,
} from '../url-tile-payloads';
import { JobProfileTile } from './JobProfileTile';

const serializePreferredLanguage = (baseValue: string, preferredLanguage: string | undefined): string =>
    is.nonEmptyString(preferredLanguage) ? `${baseValue}.lang.${preferredLanguage}` : baseValue;

const tokenValue = (tokens: readonly string[], key: string): string | undefined => {
    const keyIndex = tokens.indexOf(key);

    return keyIndex < 0 ? undefined : tokens[keyIndex + 1];
};

type JsaProfileTileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: JsaProfileDetailTilePayload;
};

type JsaFamilyTileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: JsaFamilyDetailTilePayload;
};

type JsaClusterTileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: JsaClusterDetailTilePayload;
};

type JsaCompareTileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: JsaCompareTilePayload;
};

type JsaChangelogTileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: JsaChangelogTilePayload;
};

type JsaArchiveTileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: JsaArchiveTilePayload;
};

export const jsaProfileDetailUrlTileDefinition = createUrlTileDefinition({
    maxWidth: '50%',
    minWidth: '50%',
    parseTokens: (tokens) => {
        const payload = {
            architectureId: tokens[4],
            itemType: tokens[5],
            mode: tokens[7],
            preferredLanguage: tokenValue(tokens, 'lang'),
            profileId: tokens[6],
            type: tokens.slice(0, 4).join('.'),
            version: versionTokenToNumber(tokens[8]),
        };

        return {
            ...payload,
            ...(tokenValue(tokens, 'archived') === 'true' ? { isArchived: true } : {}),
            ...(tokenValue(tokens, 'comments') === 'open' ? { isCommentsOpen: true } : {}),
        };
    },
    render: ({ context, payload }: JsaProfileTileProps) => (
        <JobProfileTile close={() => context.closeTile(JSA_PRIMARY_DETAIL_TILE_SLOT_KEY)} payload={payload} />
    ),
    schema: jsaProfileDetailTileSchema,
    serialize: (payload) => {
        const archivedSuffix = payload.isArchived === true ? '.archived.true' : '';
        const commentsSuffix = payload.isCommentsOpen === true ? '.comments.open' : '';
        const baseValue = `${payload.type}.${payload.architectureId}.${payload.itemType}.${payload.profileId}.${payload.mode}.v${payload.version}${archivedSuffix}${commentsSuffix}`;

        return serializePreferredLanguage(baseValue, payload.preferredLanguage);
    },
    slotKey: () => JSA_PRIMARY_DETAIL_TILE_SLOT_KEY,
    tileKey: (payload) => `${JSA_PRIMARY_DETAIL_TILE_SLOT_KEY}.${payload.architectureId}.${payload.profileId}`,
    toProps: (payload, context) => ({ context, payload }),
    type: JSA_PROFILE_DETAIL_TILE_TYPE,
});

export const jsaFamilyDetailUrlTileDefinition = createUrlTileDefinition({
    maxWidth: '50%',
    minWidth: '50%',
    parseTokens: (tokens) => ({
        architectureId: tokens[4],
        familyId: tokens[5],
        mode: tokens[6],
        type: tokens.slice(0, 4).join('.'),
    }),
    render: ({ context, payload }: JsaFamilyTileProps) => (
        <JobArchitectureScopeProvider architectureId={payload.architectureId}>
            <JobFamilyDetails
                architectureId={payload.architectureId}
                familyId={payload.familyId}
                mode={payload.mode}
                onClose={() => context.closeTile(JSA_PRIMARY_DETAIL_TILE_SLOT_KEY)}
            />
        </JobArchitectureScopeProvider>
    ),
    schema: jsaFamilyDetailTileSchema,
    serialize: (payload) => `${payload.type}.${payload.architectureId}.${payload.familyId}.${payload.mode}`,
    slotKey: () => JSA_PRIMARY_DETAIL_TILE_SLOT_KEY,
    tileKey: (payload) => `${JSA_PRIMARY_DETAIL_TILE_SLOT_KEY}.${payload.architectureId}.${payload.familyId}`,
    toProps: (payload, context) => ({ context, payload }),
    type: JSA_FAMILY_DETAIL_TILE_TYPE,
});

export const jsaClusterDetailUrlTileDefinition = createUrlTileDefinition({
    maxWidth: '50%',
    minWidth: '50%',
    parseTokens: (tokens) => ({
        architectureId: tokens[4],
        clusterId: tokens[6],
        familyId: tokens[5],
        mode: tokens[7],
        type: tokens.slice(0, 4).join('.'),
    }),
    render: ({ context, payload }: JsaClusterTileProps) => (
        <JobArchitectureScopeProvider architectureId={payload.architectureId}>
            <JobClusterDetails
                architectureId={payload.architectureId}
                clusterId={payload.clusterId}
                familyId={payload.familyId}
                mode={payload.mode}
                onClose={() => context.closeTile(JSA_PRIMARY_DETAIL_TILE_SLOT_KEY)}
            />
        </JobArchitectureScopeProvider>
    ),
    schema: jsaClusterDetailTileSchema,
    serialize: (payload) =>
        `${payload.type}.${payload.architectureId}.${payload.familyId}.${payload.clusterId}.${payload.mode}`,
    slotKey: () => JSA_PRIMARY_DETAIL_TILE_SLOT_KEY,
    tileKey: (payload) => `${JSA_PRIMARY_DETAIL_TILE_SLOT_KEY}.${payload.architectureId}.${payload.clusterId}`,
    toProps: (payload, context) => ({ context, payload }),
    type: JSA_CLUSTER_DETAIL_TILE_TYPE,
});

export const jsaCompareUrlTileDefinition = createUrlTileDefinition({
    maxWidth: '50%',
    minWidth: '50%',
    parseTokens: (tokens) => ({
        architectureId: tokens[3],
        itemType: tokens[4],
        preferredLanguage: tokenValue(tokens, 'lang'),
        profileId: tokens[5],
        type: tokens.slice(0, 3).join('.'),
        versionA: versionTokenToString(tokens[6]),
        versionB: versionTokenToString(tokens[7]),
    }),
    render: ({ context, payload }: JsaCompareTileProps) => (
        <JobArchitectureScopeProvider architectureId={payload.architectureId}>
            <CompareVersion
                architectureId={payload.architectureId}
                itemId={payload.profileId}
                itemType={payload.itemType}
                preferredLanguage={payload.preferredLanguage}
                versionA={payload.versionA}
                versionB={payload.versionB}
                onClose={() => context.closeTile(JSA_COMPARE_TILE_SLOT_KEY)}
            />
        </JobArchitectureScopeProvider>
    ),
    schema: jsaCompareTileSchema,
    serialize: (payload) => {
        const baseValue = `${payload.type}.${payload.architectureId}.${payload.itemType}.${payload.profileId}.v${payload.versionA}.v${payload.versionB}`;

        return serializePreferredLanguage(baseValue, payload.preferredLanguage);
    },
    slotKey: () => JSA_COMPARE_TILE_SLOT_KEY,
    tileKey: (payload) => `${JSA_COMPARE_TILE_SLOT_KEY}.${payload.architectureId}.${payload.profileId}`,
    toProps: (payload, context) => ({ context, payload }),
    type: JSA_COMPARE_TILE_TYPE,
});

export const jsaChangelogUrlTileDefinition = createUrlTileDefinition({
    maxWidth: JSA_CHANGELOG_TILE_WIDTH,
    minWidth: JSA_CHANGELOG_TILE_WIDTH,
    parseTokens: (tokens) => ({
        architectureId: tokens[3],
        type: tokens.slice(0, 3).join('.'),
    }),
    render: ({ context, payload }: JsaChangelogTileProps) => (
        <JobArchitectureScopeProvider architectureId={payload.architectureId}>
            <ChangelogDetails onClose={() => context.closeTile(JSA_CHANGELOG_TILE_SLOT_KEY)} />
        </JobArchitectureScopeProvider>
    ),
    schema: jsaChangelogTileSchema,
    serialize: (payload) => `${payload.type}.${payload.architectureId}`,
    slotKey: () => JSA_CHANGELOG_TILE_SLOT_KEY,
    tileKey: (payload) => `${JSA_CHANGELOG_TILE_SLOT_KEY}.${payload.architectureId}`,
    toProps: (payload, context) => ({ context, payload }),
    type: JSA_CHANGELOG_TILE_TYPE,
});

export const jsaArchiveUrlTileDefinition = createUrlTileDefinition({
    maxWidth: JSA_ARCHIVE_TILE_MAX_WIDTH,
    minWidth: JSA_ARCHIVE_TILE_MAX_WIDTH,
    parseTokens: (tokens) => ({
        architectureId: tokens[3],
        type: tokens.slice(0, 3).join('.'),
    }),
    render: ({ context, payload }: JsaArchiveTileProps) => (
        <JobArchitectureScopeProvider architectureId={payload.architectureId}>
            <ArchivedItemsPanel onClose={() => context.closeTile(JSA_ARCHIVE_TILE_SLOT_KEY)} />
        </JobArchitectureScopeProvider>
    ),
    schema: jsaArchiveTileSchema,
    serialize: (payload) => `${payload.type}.${payload.architectureId}`,
    slotKey: () => JSA_ARCHIVE_TILE_SLOT_KEY,
    tileKey: (payload) => `${JSA_ARCHIVE_TILE_SLOT_KEY}.${payload.architectureId}`,
    toProps: (payload, context) => ({ context, payload }),
    type: JSA_ARCHIVE_TILE_TYPE,
});

export const jobArchitectureUrlTileDefinitions: readonly UrlTileDefinition[] = [
    jsaProfileDetailUrlTileDefinition,
    jsaFamilyDetailUrlTileDefinition,
    jsaClusterDetailUrlTileDefinition,
    jsaCompareUrlTileDefinition,
    jsaChangelogUrlTileDefinition,
    jsaArchiveUrlTileDefinition,
];
